import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Shield, AlertTriangle, XCircle, CheckCircle, Activity,
  ArrowUp, Lock, Unlock, ChevronRight,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { API_BASE as API } from '../config';

function getToken() {
  return localStorage.getItem("token");
}

function headers() {
  return { Authorization: `Bearer ${getToken()}` };
}

// ─── Risk Engine Panel ───
export function RiskEnginePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/risk-engine/status`, { headers: headers() });
      setData(r.data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { fetch(); const t = setInterval(fetch, 15000); return () => clearInterval(t); }, [fetch]);

  const runCheck = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/risk-engine/check`, {}, { headers: headers() });
      setData(prev => ({ ...prev, portfolio_check: r.data }));
      if (r.data.kill_switch) {
        toast.error("KILL SWITCH ACTIVATED — All agents paused");
      } else if (r.data.safe) {
        toast.success("Portfolio risk check: ALL SAFE");
      } else {
        toast.warning("Risk alerts detected");
      }
    } catch (e) {
      toast.error("Risk check failed");
    }
    setLoading(false);
  };

  const resetKill = async () => {
    try {
      await axios.post(`${API}/api/risk-engine/reset-kill-switch`, {}, { headers: headers() });
      toast.success("Kill switch reset");
      fetch();
    } catch (e) {
      toast.error("Reset failed");
    }
  };

  const re = data?.risk_engine || {};
  const pc = data?.portfolio_check || {};
  const metrics = pc.metrics || {};
  const alerts = pc.alerts || re.recent_alerts || [];
  const killActive = re.kill_switch_active;

  return (
    <Card className="bg-zinc-950 border-zinc-800" data-testid="risk-engine-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-bold flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Shield className={`w-4 h-4 ${killActive ? "text-red-500" : "text-emerald-400"}`} />
            <span className="text-zinc-200">Risk Engine</span>
            {killActive && <Badge className="bg-red-500/20 text-red-400 border-red-500/40 text-[10px]">KILL SWITCH</Badge>}
            {!killActive && pc.safe && <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/40 text-[10px]">SAFE</Badge>}
          </span>
          <div className="flex gap-1">
            {killActive && (
              <Button size="sm" variant="outline" onClick={resetKill} className="h-6 text-[10px] px-2 border-red-700 text-red-400" data-testid="reset-kill-btn">
                <Unlock className="w-3 h-3 mr-1" /> Reset Kill
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={runCheck} disabled={loading} className="h-6 text-[10px] px-2 border-zinc-700 text-zinc-400" data-testid="run-risk-check-btn">
              <Activity className="w-3 h-3 mr-1" /> Check
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Limits Grid */}
        <div className="grid grid-cols-4 gap-2" data-testid="risk-limits-grid">
          <MetricBox label="Daily Loss" value={`${metrics.daily_loss_pct || 0}%`} limit={`${re.limits?.max_daily_loss_pct || 5}%`} warn={metrics.daily_loss_pct > (re.limits?.max_daily_loss_pct || 5) * 0.7} />
          <MetricBox label="Weekly Loss" value={`${metrics.weekly_loss_pct || 0}%`} limit={`${re.limits?.max_weekly_loss_pct || 10}%`} warn={metrics.weekly_loss_pct > (re.limits?.max_weekly_loss_pct || 10) * 0.7} />
          <MetricBox label="Portfolio DD" value={`${metrics.portfolio_dd_pct || 0}%`} limit={`${re.limits?.max_portfolio_dd_pct || 13}%`} warn={metrics.portfolio_dd_pct > (re.limits?.max_portfolio_dd_pct || 13) * 0.7} />
          <MetricBox label="Heat" value={`${metrics.portfolio_heat_pct || 0}%`} limit={`${re.limits?.max_portfolio_heat_pct || 30}%`} warn={metrics.portfolio_heat_pct > (re.limits?.max_portfolio_heat_pct || 30) * 0.7} />
        </div>
        {/* Alerts */}
        {alerts.length > 0 && (
          <div className="space-y-1 max-h-24 overflow-y-auto" data-testid="risk-alerts">
            {alerts.slice(-5).map((a, i) => (
              <div key={i} className={`flex items-center gap-2 text-[10px] p-1.5 rounded-sm ${
                a.level === "KILL" ? "bg-red-500/10 text-red-400" :
                a.level === "CRITICAL" ? "bg-amber-500/10 text-amber-400" :
                "bg-zinc-900 text-zinc-500"
              }`}>
                {a.level === "KILL" ? <XCircle className="w-3 h-3 shrink-0" /> :
                 a.level === "CRITICAL" ? <AlertTriangle className="w-3 h-3 shrink-0" /> :
                 <CheckCircle className="w-3 h-3 shrink-0" />}
                <span className="truncate">{a.message}</span>
              </div>
            ))}
          </div>
        )}
        {alerts.length === 0 && (
          <div className="text-[10px] text-zinc-600 text-center py-2">No alerts — system healthy</div>
        )}
      </CardContent>
    </Card>
  );
}

function MetricBox({ label, value, limit, warn }) {
  return (
    <div className={`p-2 rounded-sm text-center ${warn ? "bg-amber-950/20 border border-amber-800/30" : "bg-zinc-900/60 border border-zinc-800/40"}`}>
      <div className="text-[10px] text-zinc-500">{label}</div>
      <div className={`text-sm font-mono font-bold ${warn ? "text-amber-400" : "text-zinc-300"}`}>{value}</div>
      <div className="text-[9px] text-zinc-600">limit: {limit}</div>
    </div>
  );
}


// ─── Promotion Ladder Panel ───
export function PromotionLadderPanel() {
  const [levels, setLevels] = useState([]);
  const [rules, setRules] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/promotion/levels`, { headers: headers() });
        setLevels(r.data.levels || []);
        setRules(r.data.rules || {});
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const levelColors = {
    CANDIDATE: "text-zinc-400 bg-zinc-800/40 border-zinc-700/40",
    GOLD: "text-amber-400 bg-amber-950/30 border-amber-800/40",
    DIAMOND: "text-violet-400 bg-violet-950/30 border-violet-800/40",
    EMERALD: "text-emerald-400 bg-emerald-950/30 border-emerald-800/40",
    PAPER_APPROVED: "text-cyan-400 bg-cyan-950/30 border-cyan-800/40",
    LIVE_APPROVED: "text-green-400 bg-green-950/30 border-green-800/40",
  };

  return (
    <Card className="bg-zinc-950 border-zinc-800" data-testid="promotion-ladder-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-bold flex items-center gap-2">
          <ArrowUp className="w-4 h-4 text-amber-400" />
          <span className="text-zinc-200">Promotion Ladder</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-1 flex-wrap">
          {levels.map((lvl, i) => (
            <React.Fragment key={lvl}>
              <div className={`px-2 py-1 rounded-sm border text-[10px] font-bold ${levelColors[lvl] || "text-zinc-400 bg-zinc-800/40 border-zinc-700/40"}`} title={rules[lvl]?.description || ""}>
                {lvl.replace("_", " ")}
              </div>
              {i < levels.length - 1 && <ChevronRight className="w-3 h-3 text-zinc-600" />}
            </React.Fragment>
          ))}
        </div>
        <div className="mt-2 space-y-1">
          {levels.slice(1, 4).map(lvl => (
            <div key={lvl} className="text-[10px] text-zinc-500">
              <span className={`font-bold ${levelColors[lvl]?.split(" ")[0] || "text-zinc-400"}`}>{lvl}:</span>{" "}
              {rules[lvl]?.description || ""}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}


// ─── Regime Firewall Panel ───
export function RegimeFirewallPanel() {
  const [matrix, setMatrix] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/regime-firewall/matrix`, { headers: headers() });
        setMatrix(r.data.matrix || {});
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const families = Object.entries(matrix);

  return (
    <Card className="bg-zinc-950 border-zinc-800" data-testid="regime-firewall-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-bold flex items-center gap-2">
          <Lock className="w-4 h-4 text-red-400" />
          <span className="text-zinc-200">Regime Firewall</span>
          <Badge className="bg-zinc-800 text-zinc-400 border-zinc-700 text-[10px]">{families.length} families</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="max-h-48 overflow-y-auto space-y-1.5" data-testid="firewall-entries">
          {families.map(([fam, rules]) => (
            <div key={fam} className="flex items-center gap-2 text-[10px] p-1.5 bg-zinc-900/60 rounded-sm border border-zinc-800/40">
              <span className="font-bold text-zinc-300 w-20 shrink-0">{fam}</span>
              <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30 text-[9px]">{rules.target}</Badge>
              {rules.forbidden?.length > 0 && (
                <span className="text-red-400/70">
                  <XCircle className="w-3 h-3 inline mr-0.5" />
                  {rules.forbidden.join(", ")}
                </span>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}


// ─── Decay Detector Panel ───
export function DecayDetectorPanel() {
  const [data, setData] = useState(null);

  const checkDecay = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/decay-detector/check`, { headers: headers() });
      setData(r.data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { checkDecay(); const t = setInterval(checkDecay, 30000); return () => clearInterval(t); }, [checkDecay]);

  const agents = data?.agents || {};
  const entries = Object.entries(agents);

  return (
    <Card className="bg-zinc-950 border-zinc-800" data-testid="decay-detector-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-bold flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-cyan-400" />
            <span className="text-zinc-200">Decay Detector</span>
          </span>
          <Button size="sm" variant="outline" onClick={checkDecay} className="h-6 text-[10px] px-2 border-zinc-700 text-zinc-400" data-testid="check-decay-btn">
            Scan
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {entries.length === 0 && (
          <div className="text-[10px] text-zinc-600 text-center py-3">No active agents to monitor</div>
        )}
        {entries.map(([name, info]) => {
          const statusColor = {
            HEALTHY: "text-emerald-400",
            WARNING: "text-amber-400",
            DECAYING: "text-red-400",
            ALERT: "text-red-400",
            INSUFFICIENT_DATA: "text-zinc-500",
          }[info.status] || "text-zinc-400";
          return (
            <div key={name} className="p-2 bg-zinc-900/60 rounded-sm border border-zinc-800/40 mb-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-zinc-300 truncate max-w-[200px]">{name}</span>
                <Badge className={`text-[9px] ${statusColor} bg-transparent border-current`}>{info.status}</Badge>
              </div>
              {info.live_metrics && (
                <div className="flex gap-3 mt-1 text-[10px] text-zinc-500">
                  <span>WR: {info.live_metrics.win_rate}%</span>
                  <span>DD: {info.live_metrics.drawdown_pct}%</span>
                  <span>Trades: {info.live_metrics.total_trades}</span>
                  <span>Decay: {info.decay_score}/100</span>
                </div>
              )}
              {info.critical?.length > 0 && (
                <div className="mt-1 text-[10px] text-red-400/80">{info.critical[0]}</div>
              )}
              {info.warnings?.length > 0 && (
                <div className="mt-1 text-[10px] text-amber-400/80">{info.warnings[0]}</div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
